import Swiper from 'swiper';
import { eventTypes, htmlSelectors, mediaTypes, vtoTypes } from '../constants/vto';
import { Controller } from '../utils/controller';
import * as QRCode from 'qrcode';
import { Navigation, Pagination } from 'swiper/modules';
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ymkAsyncInit: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YMK: any;
    debugVTO?: () => void;
  }
}

interface IFoundationShade {
  hex?: string;
  cooler?: string;
  lighter?: string;
  guid?: string;
  darker?: string;
  warmer?: string;
  productId?: string;
}

type FoundationShade = {
  readonly bestMatch: boolean | undefined;
  readonly hex: string;
  readonly inventoryStatus: string;
  readonly sku: string;
  readonly selectedsku: string;
  readonly title: string;
  readonly url: string;
}

type FoundationTranslations = {
  readonly cooler: string;
  readonly darker: string;
  readonly guid: string;
  readonly lighter: string;
  readonly loading: string;
  readonly seeAllShades: string;
  readonly seeBestShades: string;
  readonly warmer: string;
}
interface IFoundationData {
  readonly allowBackorder: boolean;
  readonly shades: FoundationShade[];
  readonly translations: FoundationTranslations;
}

export default class extends Controller {
  static targets = [
    'productCarousel',
    'vtoContainer',
    'launchButton',
    'vtoMobileWrapper',
    'vtoShadeCarousel',
    'variantShade',
    'shadesContainer',
    'shadesDropdownText',
    'shadesDropdownSelector',
    'shadesTab',
    'trigger',
    'pagination',
    'shadeDescription',
    'qrCode',
    'qrOverlay'
  ];
  static requiredTargets = [];

  static values = {
    autoOpen: Boolean,
    configs: {
      type: Object,
      default: {}
    },
    foundationActiveSKU: {
      type: String,
      default: ''
    },
    foundationData: {
      type: Object,
      default: {}
    },
    foundationShades: {
      type: Object,
      default: {}
    },
    foundationShadesReadableRecsValue: {
      type: Array,
      default: []
    },
    locale: {
      type: String,
      default: 'en_US'
    },
    isMobile: Boolean,
    isYMKModuleLoaded: Boolean,
    isYMKModuleOpen: Boolean,
    shadeLocalizedPath: {
      type: String,
      default: ''
    },
    shades: {
      type: Array,
      default: []
    },
    activeVariant: String,
    types: {
      type: Array,
      default: []
    },
    skincareResults: Array,
    productId: {
      type: String,
      default: ''
    },
    width: Number,
    height: Number,
    brand: String
  };

  // TARGETS
  declare paginationTarget: HTMLDivElement;
  declare shadeDescriptionTarget: HTMLSpanElement;
  declare launchButtonTarget: HTMLButtonElement;
  declare productCarouselTarget: HTMLDivElement;
  declare shadesDropdownTextTarget: HTMLSpanElement;
  declare vtoContainerTarget: HTMLDivElement;
  declare variantShadeTargets: HTMLAnchorElement[];
  declare shadesContainerTarget: HTMLDivElement;
  declare shadesDropdownSelectorTargets: HTMLAnchorElement[];
  declare shadesTabTargets: HTMLButtonElement[];
  declare triggerTarget: HTMLButtonElement;
  declare qrCodeTarget: HTMLCanvasElement;
  declare qrOverlayTarget: HTMLDivElement;
  declare hasLaunchButtonTarget: boolean;
  declare hasProductCarouselTarget: boolean;
  declare hasShadesDropdownTextTarget: boolean;
  declare hasVtoContainerTarget: boolean;
  declare hasVariantShadeTargets: boolean;
  declare hasShadesContainerTarget: boolean;
  declare hasShadesDropdownSelectorTargets: boolean;
  declare hasTriggerTarget: boolean;
  declare hasPaginationTarget: boolean;
  declare hasShadeDescriptionTarget: boolean;

  // VALUES
  private activeVariantValue: string | undefined;
  private foundationActiveSKUValue: string;
  private foundationShadesReadableRecsValue: IFoundationShade[];
  private foundationShadesValue: IFoundationShade;
  private foundationDataValue: IFoundationData;
  private configsValue: Object;
  private isYMKModuleLoadedValue: boolean;
  private localeValue: string;
  private mediaValue: string;
  private payloadEventValueCameraEnd: number;
  private payloadEventValueCameraStart: number;
  private productIdValue: string;
  private shadeLocalizedPathValue: string;
  private typesValue: string[];
  private brandValue: string;

  private shadesObserver: MutationObserver | null = null;
  private iframeObserver: MutationObserver | null = null;
  private foundationObserver: MutationObserver | null = null;
  private currentType: string;
  private foundationSwiper;

  initialize() {
    this.typesValue.forEach(type => {
      const vtoConfig = this.getConfig(this.configsValue, type);
      if (vtoConfig.enabled) {
        this.shouldAutoOpen() && this.element.addEventListener('vto:auto-launch', () => { this.launchVTO(type) });
        this.loadYMK(vtoConfig);
      }
    })

    window.debugVTO = this.debugVTO;
  }

  shouldAutoOpen() {
    const urlSearchParams = new URLSearchParams(window.location.search);

    return urlSearchParams.get("vto_open") === "1";
  }

  autoOpenUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('vto_open', '1');

    return url.toString();
  }

  generateQRCode() {
    const qrColorToken = '--desktop-vto-overlay-qr-fill';

    QRCode.toCanvas(this.qrCodeTarget, this.autoOpenUrl(), {
      width: 305,
      margin: 0,
      color: { dark: this.cssVar(qrColorToken), light: '#ffffff' },
      errorCorrectionLevel: 'L'
    });
  }

  cssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  getConfig(configs, type) {
    return {
      id: configs[`${type}_api_url`],
      api_key: configs[`${type}_api_key`],
      enabled: configs[`${type}_enabled`],
      type: type
    };
  }

  onIframeAdded(callback: (iframe: HTMLIFrameElement) => void) {
    const observer = new MutationObserver((mutations) => {
      const iframe = mutations
        .flatMap((m) => Array.from(m.addedNodes))
        .filter((n) => n instanceof Element)
        .map((node) => node.querySelector('iframe'))
        .find((el) => el instanceof HTMLIFrameElement);

      if (iframe) {
        callback(iframe);
        observer.disconnect();
      }
    });

    observer.observe(this.vtoContainerTarget, { childList: true, subtree: true });
  }

  observeScreenChanges(iframe) {
    const iframeDocument = iframe.contentWindow?.document;
    const frameContent = iframeDocument?.querySelector('.frame-content') as HTMLDivElement;
    // This needs to be executed to resize launcher page on initial render
    this.resizeVTO('INITIAL_RESIZING');

    if (this.iframeObserver) {
      this.iframeObserver.disconnect();
    }

    this.iframeObserver = new MutationObserver((mutations) => {
      if (mutations.some(this.nodeChanged)) {
        this.resizeVTO('CHANGES_DETECTED');
      }
    });

    this.iframeObserver.observe(frameContent, { childList: true, subtree: true });
  }

  nodeChanged(mutation: MutationRecord) {
    return mutation.addedNodes.length > 0;
  }

  debugVTO() {
    Object.values(eventTypes).forEach((eventName) => {
      window.YMK.addEventListener(eventName, (e) => {
        console.log(`🚨 VTO Event: ${eventName}`, e || {});
      });
    });

    console.log('🚨 VTO debugger initialized. Listening to all VTO events...');
  }

  isMobile() {
    return window.innerWidth < 1024;
  }

  isDesktop() {
    return !this.isMobile();
  }

  handleCTAClick(e) {
    const type = e.target.dataset.type;
    this.launchVTO(type);
  }

  launchVTO(type) {
    this.currentType = type;

    if (this.shouldShowQrOverlay(this.currentType)) {
      this.disableLauncher();
      this.showQrOverlay();
      return;
    }

    this.disableLauncher();
    this.hidePDPFeatures();
    this.createYMKElement();

    if (this.hasVTOLoaded()) {
      this.openVTOByType();
    }

    window.ymkAsyncInit = this.ymkAsyncInit.bind(this);
    this.isMakeup() && this.onIframeAdded(this.observeScreenChanges.bind(this));
    window.ymkAsyncInit();
    this.isFoundation() && this.openVTOByType();
  }

  ymkAsyncInit() {
    const width = this.vtoContainerTarget.offsetWidth;
    const height = this.vtoContainerTarget.offsetHeight;

    window.YMK.init({
      autoOpen: this.isMakeup(),
      showCloseBtnOnLauncher: true,
      language: this.languageMapper(this.localeValue),
      ...(this.isMakeup() && { width }),
      ...(this.isMakeup() && { height }),
      ...(this.isFoundation() && { productId: [this.productIdValue] }),
      ...(this.isFoundation() && { bottomPanelOffsetY: '244px' })
    });

    this.trackEvent({ event_action: 'cta', vto_type: this.currentType, vto_action: 'cta' });
    this.addYMKEventListeners();
  }

  shouldShowQrOverlay(type) {
    return type === vtoTypes.FOUNDATION && this.isDesktop()
  }

  hasVTOLoaded() {
    return this.isYMKModuleLoadedValue;
  }

  trackEvent(detail) {
    window.dispatchEvent(
      new CustomEvent(eventTypes.VTO_ACTIONS_TRACKING, {
        detail: detail
      })
    );
  }

  showQrOverlay() {
    document.body.style.overflow = "hidden";
    this.qrOverlayTarget.classList.add("open");
  }

  closeQrOverlay() {
    document.body.style.overflow = "";
    this.qrOverlayTarget.classList.remove("open");
  }

  disableLauncher() {
    this.launchButtonTarget.disabled = true;
  }

  enableLauncher() {
    this.launchButtonTarget.disabled = false;
  }

  handleOverlayCloseClick() {
    this.closeQrOverlay();
    this.enableLauncher();
  }

  handleOverlayBackgroundClick(e: MouseEvent) {
    const content = this.qrOverlayTarget.querySelector('.vto-qr-content');

    if (content && content.contains(e.target as Node)) {
      // prevent closing the overlay when clicked inside the modal
      return;
    }

    this.handleOverlayCloseClick();
  }

  hidePDPFeatures() {
    if (this.hasVtoContainerTarget) {
      this.vtoContainerTarget.style.display = '';
      if(this.isFoundation()) {
        document.body.style.overflow = "hidden";
      }
    }

    if (!this.isFoundation()) {
      if (this.hasProductCarouselTarget) {
        this.productCarouselTarget.style.visibility = 'hidden';
      }

      if (this.hasPaginationTarget) {
        this.paginationTarget.style.visibility = 'hidden';
      }

      // hide swiper elements when VTO is active
      const swiperElements = this.element.querySelectorAll<HTMLElement>(
        '.mobile-image-zoom-button,' +
        ' .product-details-badge-text,' +
        ' .swiper-button-next,' +
        ' .swiper.pincer-swiper.swiper-container,' +
        ' .swiper-button-prev'
      );

      swiperElements.forEach(e => e.style.visibility = 'hidden');
    }
  }

  loadYMK(config) {
    const {
      id,
      api_key,
      type
    } = config;

    if (this.shouldShowQrOverlay(type)) {
      this.generateQRCode();
    }

    if (!id || !api_key) {
      return;
    }

    const subdirectory = this.getURLSubdirectoryByType(type);
    const apiUrl = `https://plugins-media.makeupar.com/${id}${subdirectory}/sdk.js?apiKey=${api_key}`;
    const scriptLoaded = Array.from(document.getElementsByTagName('script')).find(
      (script) => script.src === apiUrl
    );

    if (scriptLoaded) {
      return;
    }

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = apiUrl;
    document.body.appendChild(s);
  }

  getURLSubdirectoryByType(type) {
    switch (type) {
      case vtoTypes.MAKEUP:
        return '';
      case vtoTypes.FOUNDATION:
        return '-shadefinder';
      case vtoTypes.SKINCARE:
        return '-skincare';
      default:
        return '';
    }
  }

  openVTOByType() {
    switch (this.currentType) {
      case vtoTypes.MAKEUP:
        return window.YMK.open();
      case vtoTypes.FOUNDATION:
        return window.YMK.open(true, null, true);
      case vtoTypes.SKINCARE:
        return window.YMK.openSkincare('capture');
      default:
        return window.YMK.open();
    }
  }

  createYMKElement() {
    const ymkElement = document.getElementById(htmlSelectors.MODULE);
    if (ymkElement != null) {
      return;
    }

    const YMKElement = document.createElement('div');
    YMKElement.id = htmlSelectors.MODULE;

    if (!this.isFoundation()) {
      const height = this.vtoContainerTarget.offsetHeight;
      YMKElement.style.height = height + 'px';
    } else {
      this.vtoContainerTarget.classList.add('vto-foundation-container')
    }

    this.vtoContainerTarget.appendChild(YMKElement);
  }

  eventToMedia(event) {
    const eventToMedia = {
      [eventTypes.LIVE_CAM_ICON_CLICKED]: mediaTypes.CAMERA,
      [eventTypes.CHOOSE_MODEL_ICON_CLICKED]: mediaTypes.MODEL,
      [eventTypes.UPLOAD_PHOTO_ICON_CLICKED]: mediaTypes.UPLOAD
    };

    return eventToMedia[event];
  }

  addYMKEventListeners() {
    const vtoEvents = [
      eventTypes.CHOOSE_MODEL_ICON_CLICKED,
      eventTypes.LIVE_CAM_ICON_CLICKED,
      eventTypes.LOAD_IMAGE_BY_MODEL,
      eventTypes.UPLOAD_PHOTO_ICON_CLICKED,
      eventTypes.CAMERA_CLOSED,
      eventTypes.CAMERA_FAILED,
      eventTypes.CAMERA_OPENED,
      eventTypes.COMPARE,
      eventTypes.COMPARE_DISABLED,
      eventTypes.SPLIT_FOUR_WAY,
      eventTypes.SPLIT_FOUR_WAY_DISABLED,
      eventTypes.PHOTO_LOADED
    ];

    vtoEvents.forEach((event) => {
      window.YMK.addEventListener(event, () => {
        this.mediaValue = this.eventToMedia(event);
        this.trackEvent({
          event_action: event,
          vto_type: this.currentType,
          vto_media_type: this.mediaValue
        });
      });
    });

    window.YMK.addEventListener(eventTypes.LOADED, () => {
      this.isYMKModuleLoadedValue = true;
      this.payloadEventValueCameraEnd = performance.now();
      const eventValue = Math.floor(
        this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart
      );

      if (this.isMakeup()) {
        window.YMK.applyMakeupBySku(this.activeVariantValue);
      }
    });

    if (this.isMakeup()) {
      this.attachVTOEventToShades();

      // Shade tabs re-render the shade list, so we use a MutationObserver
      // to reassign event listeners to new elements as they are added
      this.shadesObserver = new MutationObserver(() => {
        this.attachVTOEventToShades();
      });
      this.shadesObserver.observe(this.shadesContainerTarget, { childList: true, subtree: true });
    }

    window.YMK.addEventListener(eventTypes.SKIN_SHADE_FOUND, (result) => {
      const readableRecommendations = result.readableRecommendations.filter(res => res.productId === this.productIdValue);
      this.foundationShadesReadableRecsValue = readableRecommendations;
      this.foundationShadesValue = readableRecommendations[0];
      window.YMK.applyMakeupBySku(readableRecommendations[0].guid);
      this.createFoundationShadesOverlay();
      if (this.foundationObserver) {
        this.foundationObserver.disconnect();
      }
    });

    window.YMK.addEventListener(eventTypes.SKIN_ANALYSIS_FINISHED, () => {
      document.body.style.overflow = '';
    });


    window.YMK.addEventListener(eventTypes.SHADE_FINDER_STARTED, () => {
      this.shadeFinderCustomization();
      const overlay = document.querySelector('.vto-foundations-overlay');
      if (overlay) {
        overlay.remove();
      }
    });

    window.YMK.addEventListener(eventTypes.SHADE_FINDER_COMPLETE, () => {
      const YMKFrameContent = this.getYMKFrameContent();

      if (YMKFrameContent) {
        if (this.foundationObserver) {
          this.foundationObserver.disconnect();
        }

        this.foundationObserver = new MutationObserver(mutations => {
          if (mutations.some(this.nodeChanged)) {
            this.shadeFinderCustomization();
          }
        });

        this.foundationObserver.observe(YMKFrameContent, { childList: true, subtree: true });
      }
    })

    window.YMK.addEventListener(eventTypes.CLOSED, () => {
      this.vtoContainerTarget.style.display = '';
      this.enableLauncher();
      this.payloadEventValueCameraEnd = performance.now();

      this.trackEvent({
        event_action: eventTypes.CLOSED,
        vto_type: this.currentType,
        vto_media_type: this.mediaValue,
        event_value: Math.floor(this.payloadEventValueCameraEnd - this.payloadEventValueCameraStart)
      });

      if (this.shadesObserver) {
        this.shadesObserver.disconnect();
        this.shadesObserver = null;
      }

      if (this.iframeObserver) {
        this.iframeObserver.disconnect();
        this.iframeObserver = null;
      }

      if (this.isFoundation()) {
        window.document.body.removeAttribute('style');

        if (!this.shadeLocalizedPathValue) {
          const url = new URL(window.location.href);
          url.searchParams.delete('vto_open');
          window.location.assign(url);
          return
        }
        window.location.assign(this.shadeLocalizedPathValue);
        return;
      }

      if (this.shadeLocalizedPathValue) {
        window.location.replace(this.shadeLocalizedPathValue);
        // if location.replace fails force the page to reload after two seconds
        setTimeout(() => {
          location.reload();
        }, 2000);
      } else {
        this.resetYMK();
      }
    });
  }

  attachVTOEventToShades() {
    for (const [index, element] of this.variantShadeTargets.entries()) {
      const vtoEnabled = JSON.parse(element.dataset['vtoShadeEnabled'] ?? "null");
      delete element.dataset['phxLink'];
      delete element.dataset['phxLinkState'];

      delete this.shadesDropdownSelectorTargets[index].dataset['phxLink'];
      delete this.shadesDropdownSelectorTargets[index].dataset['phxLinkState'];
      this.shadesDropdownSelectorTargets[index].dataset.action =
        'shades-dropdown#toggleListDisplay';

      const localizedPath = element.href;
      const label = element.querySelector('label');
      element.href = '#';
      this.shadesDropdownSelectorTargets[index].href = '#';

      if (vtoEnabled && vtoEnabled.includes(vtoTypes.MAKEUP)) {
        const selectedSku = element.dataset['vtoSelectedSku'];
        const vtoSku = element.querySelector('input')?.value;
        const shadeDiv = element.querySelector('[data-shade]') as HTMLDivElement;
        const skuShade = shadeDiv?.dataset['shade'];
        const description = element.dataset['shadeDescription'];

        if (vtoSku === this.activeVariantValue) {
          this.shadeLocalizedPathValue = localizedPath;
        }

        if (label) {
          label.style.cursor = 'pointer';
        }

        element.dataset.action = 'product#updateSelectedSkuViaEvent';
        element.dataset.skuid = selectedSku;

        const changeVTOShade = () => {
          this.activeVariantValue = vtoSku;
          this.shadeLocalizedPathValue = localizedPath;
          this.shadesDropdownTextTarget.innerText = label?.title || '';
          this.setDropdownColor(skuShade);
          this.setDescription(description);
          this.unselectAll();
          this.setSelected(element, index);
          window.YMK.applyMakeupBySku(vtoSku);
          this.updateShadeParam(label?.title);
        };

        element.onclick = changeVTOShade;
        this.shadesDropdownSelectorTargets[index].onclick = changeVTOShade;
      } else {
        element.style.opacity = '0.6';

        if (label) {
          label.style.cursor = 'not-allowed';
        }
      }
    }
  }

  updateShadeParam(shade) {
    if (!shade) {
      return;
    }

    const url = new URL(window.location.href);
    const shadeParam = shade.replace(/\s+/g, '_') || '';
    url.searchParams.set('shade', shadeParam);
    window.history.replaceState({}, '', url.toString());
  }

  setDescription(description) {
    if (this.hasShadeDescriptionTarget && description) {
      this.shadeDescriptionTarget.textContent = description;
    }
  }

  setDropdownColor(color) {
    const shadesDropdown = this.triggerTarget.querySelector('[data-shade]') as HTMLDivElement;

    if (shadesDropdown && color) {
      shadesDropdown.style.backgroundColor = color;
    }
  }

  unselectAll() {
    this.variantShadeTargets.forEach((el, i) => this.removeSelected(el, i));
  }

  removeSelected(shade, index) {
    const selectedVariant = shade.querySelector('.shade-selected');

    if (selectedVariant) {
      selectedVariant.classList.remove('shade-selected');
    }

    const shadEl = this.shadesDropdownSelectorTargets[index].parentNode as HTMLElement;
    shadEl.classList.remove('checked');
  }

  setSelected(shade, index) {
    const selectedVariant = shade.querySelector('.shade-swatch');

    if (selectedVariant) {
      selectedVariant.classList.add('shade-selected');
    }

    const shadSelectorEl = this.shadesDropdownSelectorTargets[index].parentNode as HTMLElement;
    shadSelectorEl.classList.add('checked');
  }

  resetYMK() {
    const ymkElement = document.getElementById(htmlSelectors.MODULE);
    this.vtoContainerTarget.style.display = 'none';
    this.productCarouselTarget.style.visibility = 'visible';
    this.paginationTarget.style.visibility = 'visible';
    ymkElement?.remove();
    window.YMK.reset();
  }

  createFoundationShadeButton(shade: FoundationShade, swiperSlide = false) {
    const { bestMatch, hex, inventoryStatus, selectedsku, sku, title, url } = shade;

    const wrapper = document.createElement('div');
    wrapper.classList.add('shade-button-wrapper');

    if (swiperSlide) {
      wrapper.classList.add('swiper-slide');
    }

    const button = document.createElement('button');
    button.style.background = hex;

    button.classList.add('shade-swatch', 'foundation-shade');
    button.dataset.sku = sku;
    button.dataset.skuid = selectedsku;
    button.dataset.action='product#updateSelectedSkuViaEvent'

    if (bestMatch || sku === this.foundationActiveSKUValue) {
      button.classList.add('shade-selected');
      this.foundationActiveSKUValue = sku;
    }

    if (inventoryStatus !== 'active') {
      const strikeThrough = document.createElement('span');
      strikeThrough.classList.add('dropdown-list-item-swatch-inactive');
      button.appendChild(strikeThrough);
    }

    button.onclick = () => {
      this.foundationShadesButtonAction(button, sku, swiperSlide, title, url)
    }

    wrapper.appendChild(button);

    if (!swiperSlide) {
      const shadeText = document.createElement('p');
      shadeText.classList.add('foundation-shade-text');
      shadeText.innerText = title;

      wrapper.appendChild(shadeText);
    }

    return wrapper;
  }

  createFoundationShadesCarousel() {
    const parentContainer = document.createElement('div');
    parentContainer.classList.add('foundation-shades-carousel-container', 'display-none');

    const shadesCarousel = document.createElement('div');
    shadesCarousel.classList.add('foundation-shades-carousel');

    const swiperWrapper = document.createElement('div');
    swiperWrapper.classList.add('swiper-wrapper');

    shadesCarousel.appendChild(swiperWrapper);

    this.foundationShadesReadableRecsValue.forEach(shade => {
      const pdpShade = this.foundationDataValue.shades.find(s => s.sku === shade.guid);
      if (pdpShade) {
        const foundationButton = this.createFoundationShadeButton(pdpShade, true);
        swiperWrapper.appendChild(foundationButton);
      }
    })

    const nextButton = document.createElement('div');
    nextButton.classList.add('swiper-button-next', this.brandValue.toLowerCase());

    const prevButton = document.createElement('div');
    prevButton.classList.add('swiper-button-prev', this.brandValue.toLowerCase());

    const pagination = document.createElement('div');
    pagination.classList.add('swiper-pagination');

    shadesCarousel.appendChild(pagination);
    parentContainer.appendChild(shadesCarousel);
    parentContainer.appendChild(nextButton);
    parentContainer.appendChild(prevButton);

    // Swiper needs to the element on the doc before init, pop out the init into a future event loop
    setTimeout(() => {
      this.foundationSwiper = new Swiper('.foundation-shades-carousel', {
        modules: [Navigation, Pagination],
        slidesPerGroup: 6,
        slidesPerView: 6,
        spaceBetween: 16,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          clickable: true,
          el: '.swiper-pagination'
        }
      });
    }, 500)

    return parentContainer;
  }

  createFoundationShadesOverlay() {
    const shadeOverlay = document.createElement('div');
    shadeOverlay.classList.add('vto-foundations-overlay');

    const shadeOverlayContent = document.createElement('div');
    shadeOverlayContent.classList.add('vto-foundations-content');

    const shadeName = document.createElement('p');
    shadeName.classList.add('foundation-shade-name');
    const bestMatchingShade = this.foundationDataValue.shades.find(variant => variant.sku === this.foundationShadesValue.guid);
    if (bestMatchingShade?.title) {
      shadeName.innerText = bestMatchingShade.title;
    }

    const shadesContainer = document.createElement('div');
    shadesContainer.classList.add('foundation-shades-container');

    const matchingShades = this.createMatchingShades();

    matchingShades.forEach(shade => {
      if (shade) {
        const foundationButton = this.createFoundationShadeButton(shade);
        shadesContainer.appendChild(foundationButton);
      }
    })

    const shadesCarousel = this.createFoundationShadesCarousel();

    const allShades = document.createElement('button');
    allShades.classList.add('all-shades');
    allShades.innerText = this.foundationDataValue.translations.seeAllShades;
    allShades.onclick = () => {
      this.foundationShadesAllShadesAction(allShades, shadesCarousel, shadesContainer)
    }

    const addToBagButtonContainer = document.createElement('div');
    addToBagButtonContainer.classList.add('foundation-atb-container');

    const addToBag = document.querySelector('[data-product-target="addButton"]');
    const addToBagButton = addToBag ? addToBag?.cloneNode(true) : document.createElement('button');

    addToBagButtonContainer.appendChild(addToBagButton);

    shadeOverlayContent.appendChild(shadeName);
    shadeOverlayContent.appendChild(shadesContainer);
    shadeOverlayContent.appendChild(shadesCarousel);
    shadeOverlayContent.appendChild(allShades);

    shadeOverlay.appendChild(shadeOverlayContent);
    shadeOverlay.appendChild(addToBagButtonContainer);

    this.vtoContainerTarget.append(shadeOverlay);
    this.shadeFinderCustomization();

    const YMKFrameContent = this.getYMKFrameContent()

    if (YMKFrameContent) {
      const imgs =  YMKFrameContent.querySelectorAll('img');

      imgs?.forEach(img => {
        if (img.src.toLowerCase().includes('split')) {
          img.click()
        }
      })
    }
  }

  createMatchingShades(): (FoundationShade | undefined)[]  {
    const tones = ['lighter', 'cooler', 'guid', 'warmer', 'darker'];
    return tones.map((tone, i) => {
      const shade = this.foundationDataValue.shades.find(shade => shade.sku === this.foundationShadesValue[tone])

      if (shade) {
        return {
            bestMatch: i === 2,
            hex: shade?.hex,
            inventoryStatus: shade.inventoryStatus,
            selectedsku: shade?.selectedsku,
            sku: this.foundationShadesValue[tone],
            title: this.foundationDataValue.translations[tone],
            url: shade?.url
          }
      } else {
        console.info(`YouCam ${tone} sku id ${this.foundationShadesValue[tone]} not found`)
      }
    }).filter(Boolean)
  }

  resizeVTO(event_action: string, event_value = 0) {
    const YMKElement = document.getElementById(htmlSelectors.MODULE);
    const DELAY = 500;
    if (!YMKElement) {
      return;
    }
    this.payloadEventValueCameraStart = performance.now();

    this.trackEvent({
      event_action,
      vto_type: this.currentType,
      vto_media_type: this.mediaValue,
      event_value
    });

    // element is hidden so we don't see its wrong version. Once it's re-styled, we show it again.
    YMKElement.style.display = 'none';

    setTimeout(() => {
      const YMKiFrame = document.getElementById(htmlSelectors.MODULE_IFRAME) as HTMLIFrameElement;

      if (YMKiFrame) {
        const iframeDocument = YMKiFrame?.contentWindow?.document;
        const frameContent = iframeDocument?.querySelector('.frame-content') as HTMLDivElement;
        this.applyScreenAdjustments(frameContent);
        this.setElementsDimensions(YMKElement, YMKiFrame, iframeDocument);
      }

      // make element visible again
      YMKElement.style.display = '';
    }, DELAY);
  }

  applyScreenAdjustments(nodeElement) {
    const innerHTML = nodeElement.innerHTML;
    if (this.isLauncher(innerHTML)) {
      this.resizeLauncherPage(nodeElement);
    } else if (this.isChooseAModel(innerHTML)) {
      this.resizeModelImages(nodeElement);
    } else if (this.isSplitFourWay(innerHTML)) {
      this.resizeSplitFourWay(nodeElement);
    }
  }

  isLauncher(html) {
    return [
      'Try It Now',
      'Essayez-le maintenant !',
      '¡Pruébalo!',
      'Jetzt ausprobieren',
      'Prova adesso',
      'Prøv det nu',
      'Prøv nå',
      'Wypróbuj teraz',
      'SPIELE MIT FARBE',
      'PLAY WITH COLOUR',
      'ZKOUŠET ODSTÍNY',
      'GIOCA CON IL COLORE',
      'BAW SIĘ KOLOREM',
      '¡DIVIÉRTETE PROBANDO!',
      'メイクアップモードを選択',
      'Escoge tu Probador',
      'Escolher',
      '체험 모드를 선택하세요',
      "Sélectionnez votre mode d'essai",
      'Play With Color',
      '选择上妆模式',
      'トライ機能を選択',
      'Wähle Try-On Erlebnis',
      'Jouez avec la couleur',
      'Speel met kleur',
      'PLAY WITH COLOR',
      "Scegli l'Esperienza Prova",
      'Baw się kolorem',
      'Hrajte si s barvami',
      'Spiele mit Farben',
      'Juega con el color',
      'Gioca con il colore',
      'TESTER DIFFÉRENTES TEINTES'
    ].some((label) => html.includes(label));
  }

  isChooseAModel(html) {
    return [
      'CHOOSE A MODEL',
      'CHOISIR UN MODÈLE',
      'Escoge una Modelo',
      'MODEL AUSWÄHLEN',
      'SCEGLI MODELLO',
      'VÆLG EN MODEL',
      'VELG EN MODELL',
      'WYBIERZ MODELKĘ',
      'WÄHLE EIN MODEL',
      'VYBRAT MODELKU',
      'SCEGLI UN MODELLO',
      'ELIGE UNA MODELO',
      'Escolha um modelo',
      '模特兒上妝',
      '模特儿上妆',
      'モデルを選択',
      '모델 선택',
      'Wähle ein Model',
      'KIES EEN MODEL',
      'Choisir un mannequin',
      'Scegli un Modello',
      'モデルを選ぶ',
      'WYBIERZ MODELKĘ/MODELA',
      'VYBRAT MODELA/MODELKU',
      'ELIGE A UNA MODELO',
      'SCEGLI UNA MODELLA'
    ].some((label) => html.includes(label));
  }

  isSplitFourWay(html) {
    return [
      'Add Another Shade',
      'Ajouter une teinte',
      'Voeg nog een tint toe',
      'Anderen Farbton hinzufügen',
      'Dodaj kolejny odcień',
      'Přidat další odstín',
      'Añade otro tono',
      "Aggiungi un'altra tonalità"
    ].some((label) => html.includes(label));
  }

  setElementsDimensions(YMKElement, YMKiFrame, iframeDocument) {
    const height = this.vtoContainerTarget.offsetHeight;
    const width = this.vtoContainerTarget.offsetWidth;

    const YMKElementFirstDiv = YMKElement.querySelector('div');
    if (YMKElementFirstDiv) {
      YMKElementFirstDiv.style.height = height + 'px';
      YMKElementFirstDiv.style.width = width + 'px';
    }

    const frameContent = iframeDocument?.querySelector('.frame-content');
    const frameFirstDiv = frameContent && frameContent.querySelector('div');
    if (frameFirstDiv) {
      frameFirstDiv.style.height = height + 'px';
      frameFirstDiv.style.width = width + 'px';
    }

    YMKiFrame.style.height = height + 'px';
    YMKiFrame.style.width = width + 'px';

    const YMKVideoInput = document.getElementById('YMK-video-input') as HTMLIFrameElement;
    if (YMKVideoInput) {
      YMKVideoInput.style.height = height + 'px';
      YMKVideoInput.style.width = width + 'px';
    }

    const resultCanvas = iframeDocument?.getElementById('result-canvas');
    if (resultCanvas) {
      resultCanvas.style.height = height + 'px';
      resultCanvas.style.width = width + 'px';
    }
  }

  languageMapper(locale) {
    const languageMap = {
      cs_: 'cze',
      de_: 'deu',
      da_: 'dnk',
      en_: 'enu',
      es_: 'esp',
      fi_: 'fin',
      fr_: 'fra',
      it_: 'ita',
      ja_: 'jpn',
      ko_: 'kor',
      nl_: 'dut',
      no_: 'nor',
      pl_: 'pol',
      pt_: 'ptb',
      zh_: 'cht',
      zhhans: 'chs',
      zhhant: 'cht'
    };

    for (const key in languageMap) {
      if (locale.includes(key)) {
        return languageMap[key];
      }
    }

    return 'enu';
  }

  private isMakeup() {
    return this.currentType === vtoTypes.MAKEUP;
  }

  private isSkincare() {
    return this.currentType === vtoTypes.SKINCARE;
  }

  private isFoundation() {
    return this.currentType === vtoTypes.FOUNDATION;
  }

  private resizeSplitFourWay(frameContent) {
    if (this.brandValue == 'CL') {
      const height = this.vtoContainerTarget.offsetHeight;
      const buttonsContainer = frameContent.querySelector(
        '.frame-content > div > div > div:nth-child(1)'
      );
      buttonsContainer.style.height = `${height}px`;
    }
  }

  private resizeLauncherPage(frameContent) {
    if (
      this.brandValue == 'MC' &&
      (this.isMobile() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches)
    ) {
      const contentContainer = frameContent.querySelector(
        '.frame-content > div:nth-child(2) > div > div:nth-child(2)'
      );
      contentContainer.style.top = '7%';

      const title = contentContainer.firstElementChild;
      title.style.padding = '0 0 10px 0';
      title.style.fontSize = '20px';
      title.style.lineHeight = '1rem';
      title.style.maxWidth = '100%';
      title.style.textAlign = 'center';

      const orButton = frameContent.querySelector(
        '.frame-content > div:nth-child(2) > div > div:nth-child(2) > div:nth-child(3) > div:nth-child(3)'
      );
      orButton.style.padding = '13px 10px';

      if (contentContainer) {
        const lastChild = contentContainer.lastElementChild;
        if (lastChild) {
          lastChild.style.padding = '0';
        }
      }
    }

    if (this.brandValue == 'CL') {
      const title = frameContent.querySelector(
        '.frame-content > div > div > div:nth-child(2) > div:nth-child(1)'
      );
      title.style.fontSize = this.isMobile() ? '25px' : '30px';
    }

    if (this.brandValue == 'EL') {
      const container = frameContent.querySelector('.frame-content > div > div > div:nth-child(2)');
      container.style.transform = 'translateY(-50%)';
      if (this.isMobile()) {
        container.style.padding = '2rem';
      }
    }
  }

  private resizeModelImages(frameContent) {
    if (this.brandValue == 'CL') {
      const generalContainer = frameContent.querySelector(
        '.frame-content > div > div > div:nth-child(2)'
      );
      generalContainer.style.top = '13%';

      const gridContainer = frameContent.querySelector(
        '.frame-content > div > div > div:nth-child(2) > div > div:nth-child(2)'
      );
      gridContainer.style.transform = this.isMobile() ? 'scale(0.6)' : 'scale(0.7)';
      gridContainer.style.transformOrigin = 'top center';
      gridContainer.style.maxHeight = 'unset';
    }

    if (this.brandValue == 'EL') {
      if (this.isMobile() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        const container = frameContent.querySelector(
          'body > div > div > div > div > div:nth-child(3)'
        );
        container.style.top = '10%';
        container.style.transform = 'none';

        const imagesContainer = container?.querySelector('div:nth-child(2)');
        imagesContainer.style.display = 'grid';
        imagesContainer.style.gridTemplateColumns = 'repeat(3, 1fr)';
        imagesContainer.style.transform = 'scale(0.7)';
        imagesContainer.style.transformOrigin = 'top';

        const imgs = frameContent?.querySelectorAll('img');
        for (const img of imgs) {
          const imgContainer = img.parentElement.parentElement;
          imgContainer.style.width = 'auto';
          imgContainer.style.height = 'auto';
          imgContainer.style.float = 'none';
        }
      }
    }
  }

  private shadeFinderCustomization() {
    const YMKFrameContent = this.getYMKFrameContent()

    if (YMKFrameContent) {
      const contentDetecting = YMKFrameContent.querySelector<HTMLElement>('#content-detecting');
      const contentProduct = YMKFrameContent.querySelector<HTMLElement>('#content-product');
      const contentSurvey = YMKFrameContent.querySelector<HTMLElement>('#content-survey');

      const contentElement = contentDetecting ?? contentProduct ?? contentSurvey;
      const contentDivs = contentElement?.querySelectorAll('div') ?? [];

      contentDivs.forEach(div => {
        const backgroundImage = getComputedStyle(div).getPropertyValue("background-image");

        if (backgroundImage.toLowerCase().includes('/assets/back')) {
          div.style.display = 'none';
        }
      })
    }
  }

  private getYMKFrameContent() {
    const YMKiFrame = document.getElementById(htmlSelectors.MODULE_IFRAME) as HTMLIFrameElement;

    if (YMKiFrame) {
      const iframeDocument = YMKiFrame?.contentWindow?.document;
      return iframeDocument?.querySelector('.frame-content') as HTMLDivElement;
    }

    return null;
  }

  private foundationShadesAllShadesAction(allShades, shadesCarousel, shadesContainer) {
    shadesContainer.classList.toggle('display-none');
    shadesCarousel.classList.toggle('display-none');
    allShades.innerText = shadesContainer.classList.contains('display-none')
    ? this.foundationDataValue.translations.seeBestShades
    : this.foundationDataValue.translations.seeAllShades;

    if (!shadesCarousel.classList.contains('display-none')) {
      const carouselButtons = shadesCarousel.querySelectorAll('button');
      carouselButtons.forEach((carouselbutton, index) => {
        const buttonSKU = carouselbutton.dataset.sku

        if (buttonSKU === this.foundationActiveSKUValue) {
          this.foundationSwiper.slideTo(index, 0, false);
        }
      })
    }
  }

  private foundationShadesButtonAction(button, sku, swiperSlide, text, url) {
      const vtoOverlay = document.querySelector('.vto-foundations-overlay');
      if (!vtoOverlay) { return; }

      this.foundationActiveSKUValue = sku;

      const selectedShade = vtoOverlay.querySelectorAll('.foundation-shade.shade-selected');
      selectedShade.forEach(el => el.classList.remove('shade-selected'));

      const shadeName = vtoOverlay.querySelector<HTMLElement>('.foundation-shade-name');

      if (shadeName) {
        if (swiperSlide) {
          shadeName.innerText = text;
        } else {
          const matchingShade = this.foundationDataValue.shades.find(shade => shade.sku === sku);
          if (matchingShade) {
            shadeName.innerText = matchingShade.title;
          }
        }
      }

      button.classList.add('shade-selected');

      const shadeButtons = vtoOverlay.querySelectorAll<HTMLElement>('.shade-swatch.foundation-shade');
      shadeButtons.forEach(el => {
          const shadeSku = el.dataset.sku;
          if (shadeSku === sku) {
            el.classList.add('shade-selected');
          }
      })

      this.shadeLocalizedPathValue = url;
      window.YMK.applyMakeupBySku(sku);
  }
}
